import { useState, type CSSProperties, type ReactNode } from 'react';
import { useFamilyTerms } from '../lib/terms';

/**
 * `termKey` names a word in the family's own language (e.g. 'page.story_vault').
 * If that language has no approved word yet, the subtitle is simply absent —
 * the page reads in English rather than in somebody else's language.
 */
export function PageHeader({ title, lead, actions, termKey, kicker }: { title: ReactNode; lead?: ReactNode; actions?: ReactNode; termKey?: string; kicker?: string }) {
  const { term, primary } = useFamilyTerms();
  const native = termKey ? term(termKey) : undefined;
  return (
    <header className="page-header">
      <div className="spread">
        <div>
          {kicker && <span className="kicker">{kicker}</span>}
          <h1 className="display">
            {title}
            {native && (
              <span className="yo page-header-yo" lang={primary?.code}>
                {native}
              </span>
            )}
          </h1>
        </div>
        {actions && <div className="row">{actions}</div>}
      </div>
      {lead && <p className="lead">{lead}</p>}
    </header>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="loading-bars" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </span>
      <span className="muted small">{label}</span>
    </div>
  );
}

export function ErrorBox({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return (
    <div className="alert error" role="alert">
      {error}
    </div>
  );
}

export function Notice({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  return (
    <div className="alert notice" role="status">
      {text}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted">{children}</p>;
}

export function Field({ label, hint, children, id }: { label: string; hint?: string; children: ReactNode; id: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  approved: 'ok',
  verified: 'ok',
  active: 'ok',
  clear: 'ok',
  passed: 'ok',
  confirmed: 'ok',
  paid: 'ok',
  live: 'ok',
  native_review: 'warn',
  pending: 'warn',
  in_review: 'warn',
  scheduled: '',
  open: 'warn',
  triaged: 'warn',
  recorded: '',
  suspended: 'bad',
  rejected: 'bad',
  revoked: 'bad',
  failed: 'bad',
  escalated: 'bad',
  cancelled: 'bad',
  critical: 'bad',
  high: 'bad',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? '';
  return <span className={`badge ${tone}`}>{status.replace(/_/g, ' ')}</span>;
}

export function NeedsReviewBadge() {
  return (
    <span className="badge warn" title="A native Yoruba speaker has not checked this yet">
      awaiting native-speaker check
    </span>
  );
}

export function fmtDate(d: string | Date | null | undefined, withTime = true): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleString(undefined, withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' });
}

/** Just the clock time, for lists already grouped under a day heading. */
export function fmtTime(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleTimeString(undefined, { timeStyle: 'short' });
}

/**
 * A day, named the way a person would say it: "Today", "Tomorrow", then the
 * weekday and date. Grouping by this string is safe because it is derived from
 * the local calendar day, not from the timestamp.
 */
export function dayKey(d: string | Date): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}
export function dayLabel(d: string | Date): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (dayKey(date) === dayKey(today)) return 'Today';
  if (dayKey(date) === dayKey(tomorrow)) return 'Tomorrow';
  return date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

export function money(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

/**
 * "1 children" is the kind of small wrongness that makes a product feel unattended,
 * and this one sits on the first screen a family sees, beside their own child's name.
 * Irregular plurals take an explicit second argument.
 */
export function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

/**
 * A password box you can look at.
 *
 * Typing a long password blind, on a phone, in a hurry, with a child pulling at
 * your arm, is how people end up locked out of the place their family's voices
 * are kept. Hidden stays the default — someone may be standing behind you — but
 * revealing it is one tap, and the button says which state it is in rather than
 * relying on an eye icon nobody agrees on the meaning of.
 */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete = 'current-password',
  minLength,
  required,
  placeholder,
  ariaLabel,
  style,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  minLength?: number;
  required?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  style?: CSSProperties;
}) {
  const [shown, setShown] = useState(false);
  return (
    <span className="pw-wrap">
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        minLength={minLength}
        required={required}
        placeholder={placeholder}
        aria-label={ariaLabel}
        style={style}
      />
      <button
        type="button"
        className="pw-toggle"
        onClick={() => setShown(!shown)}
        aria-pressed={shown}
        // Says what the password is doing now, not what the button will do, so a
        // screen reader announces the state rather than an instruction.
        aria-label={shown ? 'Password is visible. Hide it.' : 'Password is hidden. Show it.'}
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </span>
  );
}

/**
 * When a whole page could not load.
 *
 * Seven pages used to answer a failed load with one red line on an otherwise
 * empty screen and no way forward. On a good connection you never see it. On a
 * poor one — a grandmother in Lagos, a phone on a train — it is a dead end that
 * also, wrongly, looks as though the family's material might be gone.
 *
 * So: say what happened in plain words, say plainly that nothing is lost, and
 * give a button that tries again without making anyone hunt for the reload.
 */
export function LoadFailed({ error, onRetry, what = 'this page' }: { error?: string | null; onRetry?: () => void; what?: string }) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return (
    <section className="card stack" role="alert">
      <h1>We could not load {what}</h1>
      <p className="lead">
        {offline
          ? 'Your device looks offline. Everything your family has recorded is safe — it lives on our servers, not on this phone.'
          : 'Ìran did not answer just now. Everything your family has recorded is safe; this is a problem reaching us, not a problem with your material.'}
      </p>
      {error && <p className="small muted">{error}</p>}
      <div className="row">
        {onRetry && (
          <button type="button" className="btn" onClick={onRetry}>
            Try again
          </button>
        )}
        <button type="button" className="btn ghost small" onClick={() => window.location.reload()}>
          Reload the page
        </button>
      </div>
      <p className="small muted">If it keeps happening, it is worth telling us — it is far more likely to be our fault than yours.</p>
    </section>
  );
}

/**
 * A consistent footer for every form: the real action, and a way out of it.
 *
 * Twenty-one forms in this app and exactly one of them had any way to abandon
 * what you had typed short of reloading the page. That is tolerable on a
 * two-field form and not on the ones here, where a person may have spent ten
 * minutes writing down what their grandmother said.
 *
 * The submit label is always the actual verb — "Record this story", not "Save".
 * A grandparent reading "Save" has to work out what is being saved and where;
 * the verb tells them.
 */
export function FormActions({
  submitLabel,
  busy,
  disabled,
  onClear,
  clearLabel = 'Clear',
  hint,
}: {
  submitLabel: string;
  busy?: boolean;
  disabled?: boolean;
  onClear?: () => void;
  clearLabel?: string;
  hint?: string;
}) {
  return (
    <div className="form-actions">
      <button className="btn" type="submit" disabled={busy || disabled}>
        {busy ? 'Please wait…' : submitLabel}
      </button>
      {onClear && (
        <button
          type="button"
          className="btn ghost small"
          onClick={() => {
            // Confirm only when there is something to lose. A confirm on an
            // already-empty form trains people to dismiss confirms.
            if (confirm('Clear what you have typed? This cannot be undone.')) onClear();
          }}
        >
          {clearLabel}
        </button>
      )}
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}

/**
 * Keep a draft of something long in the browser, so a closed tab, a flat
 * battery or a mis-tap does not lose it.
 *
 * Deliberately local-only and deliberately short-lived. The whole promise of
 * this product is that a family's material is theirs and private; a draft of an
 * elder's words is exactly as sensitive as the finished recording, so it is
 * never sent anywhere, and it is cleared the moment the real thing is saved.
 *
 * Storage can throw or be absent (private windows, blocked site data), and a
 * draft that cannot be kept is not a reason to break the form — every path here
 * fails quietly back to ordinary behaviour.
 */
export function useDraft<T extends Record<string, unknown>>(key: string, initial: T): [T, (next: T) => void, () => void] {
  const storageKey = `iran.draft.${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return initial;
      const saved = JSON.parse(raw) as { at: number; data: T };
      // A fortnight. Long enough to come back to on a slow week, short enough
      // that nobody finds half a story from last year in their browser.
      if (Date.now() - saved.at > 14 * 24 * 60 * 60 * 1000) return initial;
      return { ...initial, ...saved.data };
    } catch {
      return initial;
    }
  });

  const update = (next: T) => {
    setValue(next);
    try {
      const empty = Object.values(next).every((v) => v === '' || v == null);
      if (empty) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), data: next }));
    } catch {
      // Not kept between visits; still perfectly usable in this one.
    }
  };

  const clear = () => {
    setValue(initial);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Nothing to clean up.
    }
  };

  return [value, update, clear];
}
